import * as React from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash';
import { useQuery } from '@apollo/client';
import { GET_PAGINATED_DATA_IMPORT_RUNS } from '../queries.ts';
import { MasterDataImportRun } from '../types.ts';
import { NavigationContext } from '../../document/routes.tsx';
import { url } from '../../core/utils/link.ts';
import { withIcon } from '../../core/components/Icon.tsx';
import {
    faArrowRightArrowLeft,
    faBoxHeart,
    faBuilding,
    faCommentDollar,
    faContactBook,
    faCube,
    faExternalLink,
    faFileSignature,
    faList,
    faLocationDot,
    faMapMarkerAlt,
} from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../../core/components/Tooltip.tsx';
import { format } from 'date-fns';
import classnames from '../../core/utils/classnames.tsx';
import Card from '../../core/components/Card.tsx';
import ReprocessDocumentModal from '../../document/pages/Overview/internal/ReprocessDocumentModal.tsx';
import { useAssistanceContext } from '../../document/pages/Assistance/AssistanceContext.tsx';
import Button from '../../core/components/Button.tsx';
import { useNavigate } from 'react-router-dom';

interface ILookupVersionInfo {
    lookup_type: string;
    lookup_definition_id: string;
    version_id: string;
    importedAt?: string; // fetched from network for the given version_id
    isActiveVersion?: boolean; // fetched from network for the given version_id
}

interface IProps {
    versionInfo: ILookupVersionInfo[];
}

const ExternalLinkIcon = withIcon(faExternalLink);
const BuildingIcon = withIcon(faBuilding);
const ContactIcon = withIcon(faContactBook);
const LocationIcon = withIcon(faLocationDot);
const BillingIcon = withIcon(faMapMarkerAlt);
const ArticlesIcon = withIcon(faCube);
const ArticleIcon = withIcon(faBoxHeart);
const ConversionIcon = withIcon(faArrowRightArrowLeft);
const ContractIcon = withIcon(faFileSignature);
const OrderDataIcon = withIcon(faList);
const OfferDataIcon = withIcon(faCommentDollar);

const iconMap = {
    article: ArticlesIcon,
    articles: ArticleIcon,
    email: ContactIcon,
    client: BuildingIcon,
    contact: ContactIcon,
    conversion_factor: ConversionIcon,
    customer: BuildingIcon,
    delivery_address: LocationIcon,
    framework_contract: ContractIcon,
    invoice_address: BillingIcon,
    offer_matching: OfferDataIcon,
    order_matching: OrderDataIcon,
};

const getIconForLookupType = (lookupType: string) => {
    const key = Object.keys(iconMap).find((k) => lookupType.toLowerCase().includes(k));
    const IconComponent = key ? iconMap[key] : BuildingIcon;
    return <IconComponent className="text-content-default pt-0.5" />;
};

const MasterDataCard = ({ versionInfo, importLogUrl }: { versionInfo: ILookupVersionInfo; importLogUrl: string }) => {
    const { t } = useTranslation('masterdata');

    const titleIcon = getIconForLookupType(versionInfo.lookup_type);
    const title = t(`importRun.concreteLookupType.${camelCase(versionInfo.lookup_type)}`);

    return (
        <Card titleIcon={titleIcon} title={title} className="flex flex-col">
            <div className="flex justify-end -mt-8 mb-4">
                <a
                    href={importLogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex font-medium rounded-sm items-center gap-1.5 text-content-default hover:text-content-primary hover:bg-surface-medium-default cursor-pointer text-sm transition-colors px-2 py-1 dark:text-content-default"
                >
                    {t('versionSummary.importDetailsTooltip')}
                    <ExternalLinkIcon className="w-3 h-3" />
                </a>
            </div>

            <div
                className={classnames(
                    'rounded-md p-3',
                    versionInfo.isActiveVersion
                        ? 'bg-surface-success-weak border border-line-success-weak'
                        : 'bg-surface-warning-weak border border-line-warning-weak'
                )}
            >
                <div className="text-xs">
                    <span
                        className={classnames(
                            'font-medium',
                            versionInfo.isActiveVersion ? 'text-content-success-strong' : 'text-content-warning-strong'
                        )}
                    >
                        {versionInfo.isActiveVersion
                            ? t(`versionSummary.versionStatus.active`)
                            : t(`versionSummary.versionStatus.outdated`)}{' '}
                        .
                    </span>
                    {versionInfo.importedAt && (
                        <span
                            className={classnames(
                                'ml-1',
                                versionInfo.isActiveVersion
                                    ? 'text-content-success-active'
                                    : 'text-content-warning-active'
                            )}
                        >
                            {`${t('versionSummary.importedOn')} `}
                            <Tooltip content={format(versionInfo.importedAt, 'PPpp')}>
                                <span className="cursor-help font-semibold text-xs">
                                    {format(versionInfo.importedAt, "MMM d, yyyy 'at' HH:mm")}.
                                </span>
                            </Tooltip>
                        </span>
                    )}
                </div>
            </div>
        </Card>
    );
};

export const MasterDataVersionSummary = ({ versionInfo = [] }: IProps) => {
    if (versionInfo == null) {
        return null;
    }

    const { t } = useTranslation('masterdata');
    const { paths, channelId } = useContext(NavigationContext);
    const navigate = useNavigate();
    const { record, refetchData, documentConfiguration } = useAssistanceContext();
    const [isReprocessModalOpen, setIsReprocessModalOpen] = useState(false);

    // Ensure info is always displayed in the same order
    let versionInfoItems = [...versionInfo].sort((a, b) => (a.lookup_type < b.lookup_type ? -1 : 0));
    const dataImportRunIds = versionInfo.reduce((acc, infoItem) => [...acc, infoItem.version_id], []);

    const { data, loading } = useQuery(GET_PAGINATED_DATA_IMPORT_RUNS, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            // fetch all the referenced versions (= import runs)
            filters: [{ name: 'id', expression: 'eq', value: dataImportRunIds }],
            offset: 0,
            first: versionInfo.length,
        },
    });
    const dataImportRuns = (data?.dataImportRuns.edges.map((node) => node.node) as MasterDataImportRun[]) || [];

    // Enrich version info with data from fetched import runs (if any)
    if (dataImportRuns.length > 0) {
        let dataImportRunsById = {};
        for (let dataImportRun of dataImportRuns) {
            dataImportRunsById[dataImportRun.id] = dataImportRun;
        }

        versionInfoItems = versionInfoItems.map((infoItem) => ({
            ...infoItem,
            importedAt: dataImportRunsById[infoItem.version_id]?.finishedAt,
            isActiveVersion: dataImportRunsById[infoItem.version_id]?.isActiveVersion,
        }));
    }

    const outdatedVersions = versionInfoItems.filter((item) => !item.isActiveVersion);
    const outdatedOldestDate = outdatedVersions.map((item) => item.importedAt).sort()[0];

    return (
        <>
            <div className="space-y-4">
                {outdatedVersions.length > 0 && (
                    <div className="bg-surface-warning-weak border border-line-warning-weak text-content-warning-strong px-4 py-3 rounded-md text-sm flex flex-col gap-4 items-start">
                        <p>
                            {t('versionSummary.outdatedWarning', {
                                date: outdatedOldestDate ? format(outdatedOldestDate, "MMM d, yyyy 'at' HH:mm") : '',
                            })}
                        </p>
                        <Button onClick={() => setIsReprocessModalOpen(true)} disabled={loading} type="button">
                            {t('versionSummary.reprocessButton')}
                        </Button>
                    </div>
                )}
                <h2 className="text-sm text-content-strong mb-4">{t('versionSummary.description')}</h2>
                {versionInfoItems.map((versionInfo) => (
                    <MasterDataCard
                        key={versionInfo.version_id}
                        versionInfo={versionInfo}
                        importLogUrl={url(
                            paths.channelMasterdata,
                            { channelId },
                            { search: { import: versionInfo.version_id } }
                        )}
                    />
                ))}
            </div>
            {isReprocessModalOpen && (
                <ReprocessDocumentModal
                    documentType={documentConfiguration.documentType}
                    recordIds={[record.id]}
                    refetch={async () => {
                        await refetchData();
                        return navigate(
                            url(
                                documentConfiguration.ASSISTANCE_TAB_PATH,
                                { recordId: record.id, tab: 'document' },
                                { keepSearch: true, excludeSearch: ['mode'] }
                            )
                        );
                    }}
                    onClose={() => setIsReprocessModalOpen(false)}
                    onSuccess={() => setIsReprocessModalOpen(false)}
                />
            )}
        </>
    );
};
